// Pure helpers for the Farming unit editor's draft layout (fields -> blocks).
// Dependency-free (no React / webpack aliases) so they run under `node --test`.
//
// Draft shape:
//   { fields: [{ key, id|null, name, isGroup?, blocks: [{ key, id|null, name, areas, geoChanged }] }],
//     deletedBlockIds: [] }
// `isGroup` marks a field the user made (Group) this session.
// `key` is a client-only identity (ids are null for unsaved entities).

const TEMPLATE_STATE = 'template';

let keyCounter = 0;
const newKey = (prefix) => {
  keyCounter += 1;
  return prefix + '_new_' + keyCounter;
};

// A block's displayed polygon: its template's geo_data, else its newest season's
// (seasons arrive template-first, then newest sow date first).
const getBlockAreas = (block) => {
  const seasons = block?.seasons || [];
  const template = seasons.find((season) => season.state === TEMPLATE_STATE);
  const source = template || seasons[0];
  return source?.geo_data?.areas || [];
};

const draftFromTree = (tree) => ({
  fields: (tree?.field_groups || []).map((fieldGroup) => ({
    key: 'fg_' + fieldGroup.id,
    id: fieldGroup.id,
    name: fieldGroup.name || '',
    blocks: (fieldGroup.blocks || []).map((block) => ({
      key: 'b_' + block.id,
      id: block.id,
      name: block.name || '',
      areas: getBlockAreas(block),
      geoChanged: false,
    })),
  })),
  deletedBlockIds: [],
});

// A single-block field that is only its block's wrapper (named after the
// block, as the migration and the editor create them) renders as a plain block
// row; every other field — empty, several blocks, or one block in a field the
// user made or named — renders as a collapsible group.
const isGroupField = (field) =>
  field.blocks.length !== 1 ||
  Boolean(field.isGroup) ||
  field.name !== field.blocks[0].name;

const allBlocks = (draft) =>
  draft.fields.reduce((blocks, field) => blocks.concat(field.blocks), []);

const findBlockLocation = (draft, blockKey) => {
  for (const field of draft.fields) {
    const block = field.blocks.find((current) => current.key === blockKey);
    if (block) {
      return { field, block };
    }
  }
  return null;
};

// Smallest positive N for which "<prefix> N" is unused.
const nextBlockName = (draft, prefix) => {
  const used = new Set(allBlocks(draft).map((block) => block.name));
  let number = 1;
  while (used.has(prefix + ' ' + number)) {
    number += 1;
  }
  return prefix + ' ' + number;
};

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// "Field A", "Field B", … then "Field 27", … once the letters run out.
const nextFieldName = (draft, prefix) => {
  const used = new Set(draft.fields.map((field) => field.name));
  for (const letter of LETTERS) {
    if (!used.has(prefix + ' ' + letter)) {
      return prefix + ' ' + letter;
    }
  }
  let number = LETTERS.length + 1;
  while (used.has(prefix + ' ' + number)) {
    number += 1;
  }
  return prefix + ' ' + number;
};

const ownField = (block) => ({
  key: newKey('fg'),
  id: null,
  name: block.name,
  blocks: [block],
});

// Remove the given blocks from their fields. A single-block field whose block
// leaves is only that block's wrapper, so it goes too; groups are kept (even
// when they end up empty) so the user decides what happens to them.
const detachBlocks = (draft, blockKeys) => {
  const moving = new Set(blockKeys);
  const detached = [];
  const fields = [];
  draft.fields.forEach((field) => {
    const remaining = field.blocks.filter((block) => {
      if (moving.has(block.key)) {
        detached.push(block);
        return false;
      }
      return true;
    });
    const wasWrapper = !isGroupField(field) && remaining.length === 0;
    if (!wasWrapper) {
      fields.push(
        remaining.length === field.blocks.length
          ? field
          : { ...field, blocks: remaining },
      );
    }
  });
  return { draft: { ...draft, fields }, detached };
};

const addDrawnBlock = (draft, areas, blockPrefix) => {
  const block = {
    key: newKey('b'),
    id: null,
    name: nextBlockName(draft, blockPrefix),
    areas,
    geoChanged: true,
  };
  return {
    draft: { ...draft, fields: draft.fields.concat([ownField(block)]) },
    blockKey: block.key,
  };
};

const addEmptyField = (draft, fieldPrefix) => ({
  ...draft,
  fields: draft.fields.concat([
    {
      key: newKey('fg'),
      id: null,
      name: nextFieldName(draft, fieldPrefix),
      isGroup: true,
      blocks: [],
    },
  ]),
});

const groupBlocks = (draft, blockKeys, fieldPrefix) => {
  if (blockKeys.length < 2) {
    return draft;
  }
  const name = nextFieldName(draft, fieldPrefix);
  const { draft: rest, detached } = detachBlocks(draft, blockKeys);
  return {
    ...rest,
    fields: rest.fields.concat([
      { key: newKey('fg'), id: null, name, isGroup: true, blocks: detached },
    ]),
  };
};

// Ungroup removes the field (Figma); each of its blocks gets its own field
// where the field was. The save drops the field group once it is empty.
const ungroupField = (draft, fieldKey) => {
  const field = draft.fields.find((current) => current.key === fieldKey);
  if (!field || field.blocks.length === 0) {
    return draft;
  }
  const fields = [];
  draft.fields.forEach((current) => {
    if (current.key !== fieldKey) {
      fields.push(current);
      return;
    }
    current.blocks.forEach((block) => fields.push(ownField(block)));
  });
  return { ...draft, fields };
};

// Deleting a field deletes it with its blocks: production's field Delete for
// each block (the save soft-deletes their seasons). Ungroup keeps the blocks.
const deleteField = (draft, fieldKey) => {
  const field = draft.fields.find((current) => current.key === fieldKey);
  if (!field) {
    return draft;
  }
  const rest = deleteBlocks(
    draft,
    field.blocks.map((block) => block.key),
  );
  return {
    ...rest,
    fields: rest.fields.filter((current) => current.key !== fieldKey),
  };
};

const deleteBlock = (draft, blockKey) => {
  const location = findBlockLocation(draft, blockKey);
  if (!location) {
    return draft;
  }
  const { draft: rest } = detachBlocks(draft, [blockKey]);
  const deletedBlockIds = location.block.id
    ? draft.deletedBlockIds.concat([location.block.id])
    : draft.deletedBlockIds;
  return { ...rest, deletedBlockIds };
};

// targetFieldKey null = drag out of any group into the block's own new field.
const moveBlock = (draft, blockKey, targetFieldKey) => {
  const location = findBlockLocation(draft, blockKey);
  if (!location || location.field.key === targetFieldKey) {
    return draft;
  }
  if (targetFieldKey === null && !isGroupField(location.field)) {
    return draft; // already on its own
  }
  const { draft: rest, detached } = detachBlocks(draft, [blockKey]);
  if (targetFieldKey === null) {
    return { ...rest, fields: rest.fields.concat([ownField(detached[0])]) };
  }
  if (!rest.fields.some((field) => field.key === targetFieldKey)) {
    return draft; // the target was the moved block's own wrapper
  }
  return {
    ...rest,
    fields: rest.fields.map((field) =>
      field.key === targetFieldKey
        ? { ...field, blocks: field.blocks.concat(detached) }
        : field,
    ),
  };
};

// Several blocks dragged together (the selection): each moves as moveBlock.
const moveBlocks = (draft, blockKeys, targetFieldKey) =>
  blockKeys.reduce(
    (current, blockKey) => moveBlock(current, blockKey, targetFieldKey),
    draft,
  );

// A single-block field is named after its block, so renaming the block renames
// the field too when the two still match.
const renameBlock = (draft, blockKey, name) => ({
  ...draft,
  fields: draft.fields.map((field) => {
    const block = field.blocks.find((current) => current.key === blockKey);
    if (!block) {
      return field;
    }
    const followName = !isGroupField(field);
    return {
      ...field,
      name: followName ? name : field.name,
      blocks: field.blocks.map((current) =>
        current.key === blockKey ? { ...current, name } : current,
      ),
    };
  }),
});

const renameField = (draft, fieldKey, name) => ({
  ...draft,
  fields: draft.fields.map((field) =>
    field.key === fieldKey ? { ...field, name } : field,
  ),
});

const deleteBlocks = (draft, blockKeys) =>
  blockKeys.reduce((current, key) => deleteBlock(current, key), draft);

const blockAreas = (draft, blockKey) =>
  findBlockLocation(draft, blockKey)?.block.areas || [];

// One polygon of a block edited on the map (leaflet.pm edit mode).
const setBlockArea = (draft, blockKey, areaIndex, ring) =>
  setBlockAreas(
    draft,
    blockKey,
    blockAreas(draft, blockKey).map((area, index) =>
      index === areaIndex ? ring : area,
    ),
  );

// One polygon of a block erased on the map (leaflet.pm removal mode): the
// block keeps its other polygons, or is deleted with its last one.
const removeBlockArea = (draft, blockKey, areaIndex) => {
  const areas = blockAreas(draft, blockKey);
  if (areas.length <= 1) {
    return deleteBlock(draft, blockKey);
  }
  return setBlockAreas(
    draft,
    blockKey,
    areas.filter((area, index) => index !== areaIndex),
  );
};

const setBlockAreas = (draft, blockKey, areas) => ({
  ...draft,
  fields: draft.fields.map((field) => ({
    ...field,
    blocks: field.blocks.map((block) =>
      block.key === blockKey ? { ...block, areas, geoChanged: true } : block,
    ),
  })),
});

const blockBody = (block) => {
  const body = { id: block.id, name: block.name };
  if (block.id === null || block.geoChanged) {
    body.geo_data = { areas: block.areas };
  }
  return body;
};

// PUT v2/farming_units/{d}/{fu}/layout body.
// Empty fields stay in the draft while editing (blocks can be dragged back)
// but are never saved: a stored field left out of the body is deleted by the
// save once nothing points at it.
const buildLayoutBody = (draft) => ({
  fields: draft.fields
    .filter((field) => field.blocks.length > 0)
    .map((field) => ({
      id: field.id,
      name: field.name,
      blocks: field.blocks.map(blockBody),
    })),
  deleted_block_ids: draft.deletedBlockIds,
});

// Names must be non-empty before saving.
const findInvalidName = (draft) =>
  draft.fields.some(
    (field) =>
      (isGroupField(field) && !field.name.trim()) ||
      field.blocks.some((block) => !block.name.trim()),
  );

// Circle (radial draw) -> polygon ring of {latitude, longitude}.
const METERS_PER_DEGREE_LAT = 111320;
const circleToRing = (center, radiusMeters, points = 64) => {
  const ring = [];
  const latRad = (center.lat * Math.PI) / 180;
  for (let index = 0; index < points; index += 1) {
    const angle = (2 * Math.PI * index) / points;
    ring.push({
      latitude:
        center.lat + (radiusMeters * Math.cos(angle)) / METERS_PER_DEGREE_LAT,
      longitude:
        center.lng +
        (radiusMeters * Math.sin(angle)) /
          (METERS_PER_DEGREE_LAT * Math.cos(latRad)),
    });
  }
  return ring;
};

// "32.1, 34.8" -> {lat, lng}; anything else -> null.
const parseCoordinates = (text) => {
  const match = /^\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\s*$/.exec(
    text || '',
  );
  if (!match) {
    return null;
  }
  const lat = parseFloat(match[1]);
  const lng = parseFloat(match[2]);
  if (isNaN(lat) || isNaN(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return null;
  }
  return { lat, lng };
};

module.exports = {
  getBlockAreas,
  draftFromTree,
  isGroupField,
  allBlocks,
  findBlockLocation,
  nextBlockName,
  nextFieldName,
  addDrawnBlock,
  addEmptyField,
  groupBlocks,
  ungroupField,
  deleteField,
  deleteBlock,
  deleteBlocks,
  setBlockArea,
  removeBlockArea,
  moveBlock,
  moveBlocks,
  renameBlock,
  renameField,
  setBlockAreas,
  buildLayoutBody,
  findInvalidName,
  circleToRing,
  parseCoordinates,
};
